import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";

import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { isClosedStatus, type UserTaskLabel, type UserTaskProject } from "@/types/userTasks.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import { userTaskQueries, useAddProjectTask, useReopenProject, useSetLabelContext } from "./api.ts";
import { ContextEditor } from "./ContextEditor.tsx";
import { readingFailedLine } from "@/shared/userTaskProject.ts";
import { taskIndicatorParts } from "@/shared/taskFilterLabels.ts";
import { UserTaskRow } from "./UserTaskRow.tsx";

/** One titled part of the page. */
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-xs font-medium text-muted-foreground">{title}</h2>
    {children}
  </section>
);

/** A project okthx closed when its goal looked met, and the user's way to reopen it. */
const ReopenProject = ({ label }: { label: UserTaskLabel }) => {
  const reopen = useReopenProject();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium">
        Done{label.closedReason ? `: ${label.closedReason}` : ""}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        data-statsig-name="tasks-project-reopen"
        disabled={reopen.isPending}
        onClick={() => reopen.mutate({ labelId: label.id })}
      >
        Reopen
      </Button>
    </div>
  );
};

/**
 * The parts of the goal no task covers, each with an add at the user's tap. An
 * added part goes from the list at once: the reading that drops it takes a
 * while, and a second tap would add it twice. One add at a time, so each
 * add's own success is the one that hides its part.
 */
const MissingParts = ({ label, missing }: { label: UserTaskLabel; missing: string[] }) => {
  const addTask = useAddProjectTask();
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const shown = missing.filter((title) => !added.has(title));
  if (shown.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1.5">
      {shown.map((title, index) => (
        <li key={`${index}-${title}`} className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs">Missing: {title}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-statsig-name="tasks-project-add-missing"
            disabled={addTask.isPending}
            onClick={() =>
              addTask.mutate(
                { labelId: label.id, title },
                { onSuccess: () => setAdded((prev) => new Set(prev).add(title)) },
              )
            }
          >
            Add as a task
          </Button>
        </li>
      ))}
    </ul>
  );
};

/**
 * okthx's reading against the goal. A project whose goal was met is closed,
 * with a Reopen; a missing part can be added as a task, at the user's tap.
 */
const Reading = ({ project }: { project: UserTaskProject }) => {
  const setContext = useSetLabelContext();
  const { label, context, reading, readingFailed } = project;
  const closed = label.status === "done";
  // The editor shows even before okthx has read the label: a label with no
  // tasks or conversations yet gets its first context from the user. Not while
  // a reading has failed: that says why there is nothing to read, and "hasn't
  // read this yet" would be a second answer contradicting it.
  const unread = !context && !reading && !closed && !readingFailed;
  return (
    <div className={cn(cardSurfaceClass, "flex flex-col gap-2")}>
      {unread && <p className="text-sm text-muted-foreground">okthx hasn't read this label yet.</p>}
      {readingFailed && (
        <p className="text-sm text-muted-foreground">{readingFailedLine(!!context)}</p>
      )}
      <ContextEditor
        context={context}
        saving={setContext.isPending}
        onSave={(text) => setContext.mutateAsync({ labelId: label.id, text })}
        statsigName="tasks-label-context"
      />
      {closed && <ReopenProject label={label} />}
      <MissingParts label={label} missing={reading?.missing ?? []} />
    </div>
  );
};

/**
 * A label's page, in order: its goal and due date, okthx's reading, the open
 * tasks under it, its conversations and what okthx made in them, then the
 * closed tasks, which trail as the record.
 */
const ProjectBody = ({ project }: { project: UserTaskProject }) => {
  const { label, tasks, threads, artifacts } = project;
  const open = tasks.filter((t) => !isClosedStatus(t.status));
  // A dismissed suggestion is the record of a "no", not work the project did.
  const closed = tasks.filter((t) => isClosedStatus(t.status) && !t.dismissed);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
      {(label.goal || label.dueMs) && (
        <p className="text-xs text-muted-foreground">
          {label.goal && <>Goal: {label.goal}</>}
          {label.goal && label.dueMs && " · "}
          {label.dueMs &&
            `Due ${new Date(label.dueMs).toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}`}
        </p>
      )}
      <Reading project={project} />
      {open.length > 0 && (
        <Section title={`Open · ${open.length}`}>
          <ul className="flex flex-col gap-2">
            {open.map((task) => (
              <UserTaskRow
                key={task.id}
                task={task}
                group={taskIndicatorParts(task)}
                inLabelId={label.id}
              />
            ))}
          </ul>
        </Section>
      )}
      {threads.length > 0 && (
        <Section title="Conversations">
          <ul className="flex flex-col gap-1 text-sm">
            {threads.map((thread) => (
              <li key={thread.id}>
                <Link href={`/threads/${thread.id}`} className="underline-offset-2 hover:underline">
                  {thread.title}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {artifacts.length > 0 && (
        <Section title="What Okthx made">
          <ul className="flex flex-col gap-1 text-sm">
            {artifacts.map((artifact) => (
              <li key={artifact.id}>
                <span className="text-muted-foreground">{artifact.typeLabel}: </span>
                {artifact.title}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {closed.length > 0 && (
        <Section title={`Closed · ${closed.length}`}>
          <ul className="flex flex-col gap-2">
            {closed.map((task) => (
              <UserTaskRow
                key={task.id}
                task={task}
                group={taskIndicatorParts(task)}
                inLabelId={label.id}
              />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

/** A label's own page in the Tasks tab, at `/tasks/label-:id`. */
export const ProjectView = ({
  labelId,
  accountSwitcher,
  leading,
}: {
  labelId: string;
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => {
  const [, navigate] = useLocation();
  const { data, isLoading, isError } = useQuery(userTaskQueries.project(labelId));

  let body: ReactNode;
  if (isLoading) {
    body = <Loader className="py-8" />;
  } else if (isError || !data) {
    body = <p className="p-4 text-sm text-muted-foreground">Couldn't load this label.</p>;
  } else {
    body = <ProjectBody key={data.label.id} project={data} />;
  }

  return (
    <CanvasPivotPanel
      title={data?.label.name ?? "Label"}
      parentLabel="Tasks"
      onBack={() => navigate(userTaskHref())}
      backStatsigName="tasks-project-back"
      leading={leading}
      accountSwitcher={accountSwitcher}
    >
      {body}
    </CanvasPivotPanel>
  );
};
